import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Row, Col, Button, Spinner, Badge, Table, Modal,
  Nav, NavItem, NavLink, TabContent, TabPane, Card, CardBody,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import usePermission from '../Hooks/usePermission';
import api from '../helpers/api';
import {
  METER_UPDATE, METER_DELETE, METER_CREATE,
} from '../constants/permissions';
import MeterForm from './MeterForm';
import MeterReadingForm from './MeterReadingForm';

const STATUS_COLORS = {
  PENDING: 'secondary',
  STABLE: 'success',
  WARNING: 'warning',
  ALARM: 'danger',
  OVERDUE: 'info',
};

const FREQUENCY_LABEL_KEYS = {
  NONE: 'meters.freq_none',
  HOUR: 'meters.freq_hour',
  DAY: 'meters.freq_day',
  WEEK: 'meters.freq_week',
  MONTH: 'meters.freq_month',
  YEAR: 'meters.freq_year',
};

const FREQUENCY_LABEL_FALLBACKS = {
  NONE: 'None',
  HOUR: 'Hours',
  DAY: 'Days',
  WEEK: 'Weeks',
  MONTH: 'Months',
  YEAR: 'Years',
};

const MeterDetailModal = ({ isOpen, toggle, meterId, onUpdate }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();

  const [activeTab, setActiveTab] = useState('details');
  const [meter, setMeter] = useState(null);
  const [loading, setLoading] = useState(false);

  const [readings, setReadings] = useState([]);
  const [readingsLoading, setReadingsLoading] = useState(false);
  const [readingsPagination, setReadingsPagination] = useState(null);
  const [readingPage, setReadingPage] = useState(0);
  const readingPageSize = 10;

  const [showEditForm, setShowEditForm] = useState(false);
  const [showReadingForm, setShowReadingForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchMeter = useCallback(async () => {
    if (!meterId) return;
    try {
      setLoading(true);
      const { data } = await api.get(`meter/get?id=${meterId}`);
      setMeter(data.response);
    } catch {
      setMeter(null);
    } finally {
      setLoading(false);
    }
  }, [meterId]);

  const fetchReadings = useCallback(async (pg = 0) => {
    if (!meterId) return;
    try {
      setReadingsLoading(true);
      const { data } = await api.get(`meter/reading/list?meterId=${meterId}&page=${pg}&size=${readingPageSize}`);
      const response = data.response;
      setReadings(response.content || response || []);
      if (response.content) {
        setReadingsPagination({
          totalElements: response.totalElements,
          totalPages: response.totalPages,
        });
      }
    } catch {
      setReadings([]);
    } finally {
      setReadingsLoading(false);
    }
  }, [meterId]);

  useEffect(() => {
    if (isOpen && meterId) {
      setActiveTab('details');
      fetchMeter();
      setReadingPage(0);
      fetchReadings(0);
    }
  }, [isOpen, meterId, fetchMeter, fetchReadings]);

  useEffect(() => {
    if (isOpen && meterId && readingPage > 0) {
      fetchReadings(readingPage);
    }
  }, [readingPage]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`meter/delete?id=${meterId}`);
      toggle();
      if (onUpdate) onUpdate();
    } catch {
      // error handled silently
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleEditClose = () => {
    setShowEditForm(false);
    fetchMeter();
    if (onUpdate) onUpdate();
  };

  const handleReadingClose = () => {
    setShowReadingForm(false);
    fetchMeter();
    fetchReadings(0);
    setReadingPage(0);
    if (onUpdate) onUpdate();
  };

  // Chart
  const chartSvg = useMemo(() => {
    if (!readings || readings.length < 2) return null;
    const chartData = [...readings].reverse().slice(-20);
    const values = chartData.map((r) => r.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const width = 600;
    const height = 140;
    const padding = 10;
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;

    const points = chartData.map((r, i) => {
      const x = padding + (i / (chartData.length - 1)) * usableWidth;
      const y = padding + usableHeight - ((r.value - min) / range) * usableHeight;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg viewBox={`0 0 ${width} ${height}`} className="w-100" style={{ maxHeight: 160 }}>
        <polyline fill="none" stroke="#4b38b3" strokeWidth="2" points={points} />
        {meter?.warningThreshold != null && (() => {
          const y = padding + usableHeight - ((meter.warningThreshold - min) / range) * usableHeight;
          return <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f1b44c" strokeWidth="1" strokeDasharray="4,4" />;
        })()}
        {meter?.alarmThreshold != null && (() => {
          const y = padding + usableHeight - ((meter.alarmThreshold - min) / range) * usableHeight;
          return <line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f46a6a" strokeWidth="1" strokeDasharray="4,4" />;
        })()}
      </svg>
    );
  }, [readings, meter]);

  const readingTotalPages = readingsPagination?.totalPages || 1;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: -50 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.8, y: 50, transition: { duration: 0.2 } }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable className="fade-scale-modal" backdropClassName="modal-backdrop-blur">
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
              {/* Header — matching app style (gradient) */}
              <div
                className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
                style={{
                  top: 0,
                  zIndex: 10,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  padding: '1.5rem'
                }}
              >
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                         style={{ width: '48px', height: '48px' }}>
                      <FeatherIcon icon="activity" size={24} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                      <span className="me-2">{loading ? '...' : meter?.name || t('meters.title', 'Meter')}</span>
                      {meter?.status && (
                        <Badge color={STATUS_COLORS[meter.status] || 'secondary'} className="ms-1">
                          {t(`meters.status_${meter.status.toLowerCase()}`, meter.status)}
                        </Badge>
                      )}
                    </h4>
                    <div className="d-flex align-items-center gap-3">
                      {meter?.assetName && (
                        <span className="text-white-50 small">
                          <FeatherIcon icon="tool" size={14} className="me-1" />
                          {meter.assetName}
                        </span>
                      )}
                      {meter?.locationName && (
                        <span className="text-white-50 small">
                          <FeatherIcon icon="map-pin" size={14} className="me-1" />
                          {meter.locationName}
                        </span>
                      )}
                      {meter?.measurementUnit && (
                        <span className="text-white-50 small">
                          <FeatherIcon icon="hash" size={14} className="me-1" />
                          {t(`meters.unit_${meter.measurementUnit.toLowerCase().replace(/[\s/²]+/g, '_')}`, meter.measurementUnit)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 ms-auto">
                  {hasPermission(METER_CREATE) && meter && (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                      onClick={() => setShowReadingForm(true)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        color: '#3980c0',
                        fontWeight: '500',
                        borderRadius: '8px',
                      }}
                    >
                      <FeatherIcon icon="plus-circle" size={16} />
                      <span>{t('meters.record_reading', 'Record Reading')}</span>
                    </button>
                  )}
                  {hasPermission(METER_UPDATE) && meter && (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                      onClick={() => setShowEditForm(true)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        color: '#495057',
                        fontWeight: '500',
                        borderRadius: '8px',
                      }}
                    >
                      <FeatherIcon icon="edit-3" size={16} />
                      <span>{t('shared.edit', 'Edit')}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={toggle}
                    aria-label={t('shared.close', 'Close')}
                  ></button>
                </div>
              </div>

              {/* Tab Navigation */}
              <Nav tabs className="border-bottom-0 px-4 bg-light">
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveTab('details')}
                    style={{ border: 'none', borderRadius: '8px 8px 0 0', padding: '12px 20px', fontWeight: '500', cursor: 'pointer' }}
                  >
                    <FeatherIcon icon="info" size={16} className="me-2" />
                    {t('shared.details', 'Details')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={`nav-link-modern ${activeTab === 'readings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('readings')}
                    style={{ border: 'none', borderRadius: '8px 8px 0 0', padding: '12px 20px', fontWeight: '500', cursor: 'pointer' }}
                  >
                    <FeatherIcon icon="list" size={16} className="me-2" />
                    {t('meters.readings', 'Readings')}
                    {readings.length > 0 && (
                      <Badge color="primary" pill className="ms-2" style={{ fontSize: '10px' }}>
                        {readingsPagination?.totalElements || readings.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              </Nav>

              {/* Body */}
              <div
                className="modal-body p-0"
                style={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}
              >
                <TabContent activeTab={activeTab} className="h-100">

                  {/* Details Tab */}
                  <TabPane tabId="details" className="h-100">
                    {loading ? (
                      <div className="text-center py-5"><Spinner color="primary" /></div>
                    ) : meter ? (
                      <div className="p-4">
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <Card className="border-0 shadow-sm h-100">
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                       style={{ width: '32px', height: '32px' }}>
                                    <FeatherIcon icon="info" size={16} className="text-primary" />
                                  </div>
                                  <h6 className="mb-0 text-primary fw-bold">{t('shared.basic_information', 'Basic Information')}</h6>
                                </div>

                                <div className="mb-3">
                                  <small className="text-muted">{t('meters.meter_type', 'Meter Type')}</small>
                                  <div className="fw-medium">{t('meters.type_manual', 'Manual')}</div>
                                </div>
                                <div className="mb-3">
                                  <small className="text-muted">{t('meters.measurement_unit', 'Measurement Unit')}</small>
                                  <div className="fw-medium">{meter.measurementUnit ? t(`meters.unit_${meter.measurementUnit.toLowerCase().replace(/[\s/²]+/g, '_')}`, meter.measurementUnit) : '-'}</div>
                                </div>
                                <div className="mb-3">
                                  <small className="text-muted">{t('meters.asset', 'Asset')}</small>
                                  <div className="fw-medium">{meter.assetName || '-'}</div>
                                </div>
                                <div className="mb-0">
                                  <small className="text-muted">{t('meters.location', 'Location')}</small>
                                  <div className="fw-medium">{meter.locationName || '-'}</div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>

                          {/* Configuration */}
                          <Col md={6}>
                            <Card className="border-0 shadow-sm h-100">
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                  <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                       style={{ width: '32px', height: '32px' }}>
                                    <FeatherIcon icon="settings" size={16} className="text-warning" />
                                  </div>
                                  <h6 className="mb-0 text-warning fw-bold">{t('meters.configuration', 'Configuration')}</h6>
                                </div>

                                <div className="mb-3">
                                  <small className="text-muted">{t('shared.status', 'Status')}</small>
                                  <div>
                                    <Badge color={STATUS_COLORS[meter.status] || 'secondary'}>
                                      {t(`meters.status_${meter.status?.toLowerCase()}`, meter.status)}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <small className="text-muted">{t('meters.reading_frequency', 'Reading Frequency')}</small>
                                  <div className="fw-medium">
                                    {meter.readingFrequency && meter.readingFrequency !== 'NONE'
                                      ? `${t('meters.every', 'Every')} ${meter.readingFrequencyInterval || 1} ${FREQUENCY_LABEL_KEYS[meter.readingFrequency] ? t(FREQUENCY_LABEL_KEYS[meter.readingFrequency], FREQUENCY_LABEL_FALLBACKS[meter.readingFrequency]) : meter.readingFrequency}`
                                      : '-'}
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <small className="text-muted">{t('meters.warning_threshold', 'Warning Threshold')}</small>
                                  <div className="fw-medium">{meter.warningThreshold != null ? meter.warningThreshold : '-'}</div>
                                </div>
                                <div className="mb-0">
                                  <small className="text-muted">{t('meters.alarm_threshold', 'Alarm Threshold')}</small>
                                  <div className="fw-medium">{meter.alarmThreshold != null ? meter.alarmThreshold : '-'}</div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Description */}
                        {meter.description && (
                          <Card className="border-0 shadow-sm mt-4">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-3">
                                <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                     style={{ width: '32px', height: '32px' }}>
                                  <FeatherIcon icon="file-text" size={16} className="text-info" />
                                </div>
                                <h6 className="mb-0 text-info fw-bold">{t('meters.description', 'Description')}</h6>
                              </div>
                              <p className="mb-0">{meter.description}</p>
                            </CardBody>
                          </Card>
                        )}

                        {/* Trend Chart */}
                        {chartSvg && (
                          <Card className="border-0 shadow-sm mt-4">
                            <CardBody className="p-4">
                              <div className="d-flex align-items-center mb-3">
                                <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                     style={{ width: '32px', height: '32px' }}>
                                  <FeatherIcon icon="trending-up" size={16} className="text-success" />
                                </div>
                                <h6 className="mb-0 text-success fw-bold">{t('meters.reading_trend', 'Reading Trend')}</h6>
                              </div>
                              {chartSvg}
                              <div className="d-flex mt-2">
                                <small className="text-muted">
                                  {meter.warningThreshold != null && (
                                    <span className="me-3"><span style={{ color: '#f1b44c' }}>---</span> {t('meters.warning_threshold', 'Warning')}</span>
                                  )}
                                  {meter.alarmThreshold != null && (
                                    <span><span style={{ color: '#f46a6a' }}>---</span> {t('meters.alarm_threshold', 'Alarm')}</span>
                                  )}
                                </small>
                              </div>
                            </CardBody>
                          </Card>
                        )}

                        {/* Delete action */}
                        {hasPermission(METER_DELETE) && (
                          <div className="mt-4 pt-3 border-top text-end">
                            <Button color="outline-danger" size="sm" onClick={() => setDeleteConfirm(true)}>
                              <FeatherIcon icon="trash-2" size={14} className="me-1" />
                              {t('meters.delete_meter', 'Delete Meter')}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-5 text-muted">{t('meters.not_found', 'Meter not found.')}</div>
                    )}
                  </TabPane>

                  {/* Readings Tab */}
                  <TabPane tabId="readings" className="h-100">
                    <div className="p-4">
                      {readingsLoading ? (
                        <div className="text-center py-5"><Spinner color="primary" /></div>
                      ) : !readings || readings.length === 0 ? (
                        <div className="text-center py-5">
                          <FeatherIcon icon="activity" size={48} className="text-muted mb-3" />
                          <h5 className="text-muted">{t('meters.no_readings', 'No readings recorded yet.')}</h5>
                          {hasPermission(METER_CREATE) && (
                            <Button color="primary" size="sm" className="mt-2" onClick={() => setShowReadingForm(true)}>
                              <FeatherIcon icon="plus-circle" size={14} className="me-1" />
                              {t('meters.record_reading', 'Record Reading')}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-3">
                              <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-2"
                                   style={{ width: '32px', height: '32px' }}>
                                <FeatherIcon icon="list" size={16} className="text-primary" />
                              </div>
                              <h6 className="mb-0 text-primary fw-bold">{t('meters.reading_history', 'Reading History')}</h6>
                            </div>
                            <div className="table-responsive">
                              <Table hover className="mb-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th>{t('meters.reading_value', 'Value')}</th>
                                    <th>{t('shared.status', 'Status')}</th>
                                    <th>{t('meters.reading_source', 'Source')}</th>
                                    <th>{t('meters.recorded_by', 'Recorded By')}</th>
                                    <th>{t('meters.date', 'Date')}</th>
                                    <th>{t('meters.work_order', 'Work Order')}</th>
                                    <th>{t('meters.reading_notes', 'Notes')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {readings.map((reading) => (
                                    <tr key={reading.id}>
                                      <td className="fw-semibold">{reading.value}</td>
                                      <td>
                                        <Badge color={STATUS_COLORS[reading.resultingStatus] || 'secondary'}>
                                          {reading.resultingStatus ? t(`meters.status_${reading.resultingStatus.toLowerCase()}`, reading.resultingStatus) : '-'}
                                        </Badge>
                                      </td>
                                      <td>{reading.source ? t(`meters.source_${reading.source.toLowerCase().replace(/[\s]+/g, '_')}`, reading.source) : '-'}</td>
                                      <td>{reading.recordedByName || '-'}</td>
                                      <td>
                                        {reading.createdAt
                                          ? `${new Date(reading.createdAt).toLocaleDateString('en-GB')} ${new Date(reading.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                          : '-'}
                                      </td>
                                      <td>{reading.workOrderNumber || (reading.workOrderId ? `WO-${reading.workOrderId}` : '-')}</td>
                                      <td className="small">{reading.notes || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>

                            {readingTotalPages > 1 && (
                              <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                  {t('meters.showing', 'Showing')} {readings.length} {t('shared.of', 'of')}{' '}
                                  {readingsPagination?.totalElements || readings.length}
                                </small>
                                <div className="d-flex gap-1">
                                  <Button color="outline-secondary" size="sm" disabled={readingPage === 0}
                                    onClick={() => setReadingPage((p) => p - 1)}>
                                    <FeatherIcon icon="chevron-left" size={16} />
                                  </Button>
                                  <span className="d-flex align-items-center px-2 small">
                                    {readingPage + 1} / {readingTotalPages}
                                  </span>
                                  <Button color="outline-secondary" size="sm" disabled={readingPage + 1 >= readingTotalPages}
                                    onClick={() => setReadingPage((p) => p + 1)}>
                                    <FeatherIcon icon="chevron-right" size={16} />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      )}
                    </div>
                  </TabPane>
                </TabContent>
              </div>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <Modal isOpen={deleteConfirm} toggle={() => setDeleteConfirm(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('meters.confirm_delete_title', 'Delete Meter')}</h5>
          <button type="button" className="btn-close" onClick={() => setDeleteConfirm(false)}></button>
        </div>
        <div className="modal-body">
          {t('meters.confirm_delete', 'Are you sure you want to delete this meter?')}
          <p className="fw-bold mt-2">{meter?.name}</p>
        </div>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => setDeleteConfirm(false)}>{t('shared.cancel', 'Cancel')}</Button>
          <Button color="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </Button>
        </div>
      </Modal>

      {/* Edit Form */}
      {meter && (
        <MeterForm isOpen={showEditForm} toggle={() => setShowEditForm(false)} editData={meter} onSave={handleEditClose} />
      )}

      {/* Reading Form */}
      {meter && (
        <MeterReadingForm isOpen={showReadingForm} toggle={() => setShowReadingForm(false)} meterId={meter.id} meterName={meter.name} onSave={handleReadingClose} />
      )}
    </>
  );
};

export default MeterDetailModal;
